import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Input, Button, Message } from '@arco-design/web-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { initializeSystem } from '@/api/setup'

export default function Setup() {
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const setupMutation = useMutation({
    mutationFn: initializeSystem,
    onSuccess: () => {
      queryClient.setQueryData(['setupStatus'], { initialized: true })
      queryClient.invalidateQueries({ queryKey: ['setupStatus'] })
      Message.success('系统初始化成功，请使用管理员账号登录')
      navigate('/?auth=login')
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } }; message?: string }
      Message.error(err.response?.data?.message || err.message || '初始化失败')
    },
  })

  const handleSubmit = async (values: {
    username: string
    password: string
    confirmPassword: string
    displayName?: string
  }) => {
    setLoading(true)
    try {
      await setupMutation.mutateAsync({
        username: values.username,
        password: values.password,
        confirmPassword: values.confirmPassword,
        displayName: values.displayName,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#f8fafa]">
      <div className="w-full max-w-[440px]">
        {/* Brand Header Section - 左右布局 */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-4">
            {/* Logo */}
            <img
              src="/logo.svg"
              alt="TokenTCP"
              className="w-12 h-12"
            />
            {/* 文字区域 */}
            <div className="text-left">
              <h1 className="text-2xl font-extrabold text-[#191c1d] tracking-tight">TokenTCP</h1>
              <p className="text-[#6f7976] text-sm mt-0.5">系统初始化 - 创建管理员账号</p>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl p-8 shadow-lg shadow-[#005447]/5">
          <Form form={form} onSubmit={handleSubmit} className="space-y-5">
            {/* Username Field */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#6f7976] ml-1">
                管理员用户名
              </label>
              <Form.Item field="username" rules={[{ required: true, message: '请输入用户名' }]} noStyle>
                <Input
                  placeholder="输入管理员账号"
                  size="large"
                  style={{
                    backgroundColor: '#f2f4f4',
                    border: 'none',
                    borderRadius: '8px',
                    height: '48px',
                    fontSize: '14px'
                  }}
                  prefix={
                    <svg className="w-5 h-5 text-[#6f7976] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  }
                />
              </Form.Item>
            </div>

            {/* Display Name Field */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#6f7976] ml-1">
                显示名称（可选）
              </label>
              <Form.Item field="displayName" noStyle>
                <Input
                  placeholder="输入显示名称"
                  size="large"
                  style={{
                    backgroundColor: '#f2f4f4',
                    border: 'none',
                    borderRadius: '8px',
                    height: '48px',
                    fontSize: '14px'
                  }}
                  prefix={
                    <svg className="w-5 h-5 text-[#6f7976] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                    </svg>
                  }
                />
              </Form.Item>
            </div>

            {/* Password Field */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#6f7976] ml-1">
                密码
              </label>
              <Form.Item field="password" rules={[{ required: true, message: '请输入密码' }]} noStyle>
                <Input.Password
                  placeholder="输入密码"
                  size="large"
                  style={{
                    backgroundColor: '#f2f4f4',
                    border: 'none',
                    borderRadius: '8px',
                    height: '48px',
                    fontSize: '14px'
                  }}
                  prefix={
                    <svg className="w-5 h-5 text-[#6f7976] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  }
                />
              </Form.Item>
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#6f7976] ml-1">
                确认密码
              </label>
              <Form.Item
                field="confirmPassword"
                rules={[
                  { required: true, message: '请再次输入密码' },
                  {
                    validator: (value, callback) => {
                      if (!value) {
                        callback()
                        return
                      }
                      if (value !== (form.getFieldValue('password') as string)) {
                        callback('两次输入的密码不一致')
                        return
                      }
                      callback()
                    },
                  },
                ]}
                noStyle
              >
                <Input.Password
                  placeholder="再次输入密码"
                  size="large"
                  style={{
                    backgroundColor: '#f2f4f4',
                    border: 'none',
                    borderRadius: '8px',
                    height: '48px',
                    fontSize: '14px'
                  }}
                  prefix={
                    <svg className="w-5 h-5 text-[#6f7976] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  }
                />
              </Form.Item>
            </div>

            {/* Action Area */}
            <div className="pt-3">
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading || setupMutation.isPending}
                style={{
                  width: '100%',
                  height: '48px',
                  background: 'linear-gradient(135deg, #005447 0%, #106e5e 100%)',
                  border: 'none',
                  borderRadius: '24px',
                  fontSize: '16px',
                  fontWeight: 600,
                  boxShadow: '0 4px 14px rgba(0, 84, 71, 0.3)'
                }}
              >
                初始化系统
              </Button>
            </div>
          </Form>
        </div>

        {/* Footer */}
        <div className="mt-10 flex flex-col items-center space-y-4">
          <div className="flex items-center space-x-3 text-[#bec9c4]/60">
            <div className="h-px w-8 bg-current"></div>
            <span className="text-[10px] font-bold uppercase tracking-widest">TokenTCP AI Platform</span>
            <div className="h-px w-8 bg-current"></div>
          </div>
        </div>
      </div>
    </div>
  )
}
